import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Download, Mail, Trash2, FileText, CheckCircle2, FolderDown } from "lucide-react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { savePdf } from "@/lib/savePdf";
import { shareOrMailtoPdf } from "@/lib/sharePdf";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import EmailRecipientDialog from "@/components/EmailRecipientDialog";

export default function InvoicesPage() {
  const { user } = useAuth();
  const isClient = user?.role === "client";
  const [invs, setInvs] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [open, setOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(null);
  const [form, setForm] = useState({ worker_id: "", week_start: "", week_end: "" });
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [approving, setApproving] = useState(false);

  const load = async () => {
    setInvs((await api.get("/invoices")).data);
    setWorkers((await api.get("/workers")).data);
  };
  useEffect(() => { load(); }, []);

  // Re-runs the preview whenever the dialog is open and worker + dates are set.
  // Called explicitly from the dialog's open handler and from the worker/date
  // change handlers — avoids the react-hooks/set-state-in-effect lint rule which
  // (correctly) discourages doing async setState directly inside useEffect.
  const fetchPreview = async (next) => {
    const f = next || form;
    if (!f.worker_id || !f.week_start || !f.week_end) {
      setPreview(null);
      return;
    }
    setPreviewing(true);
    try {
      const { data } = await api.get("/invoices/preview", { params: f });
      setPreview(data);
    } catch {
      setPreview(null);
    } finally {
      setPreviewing(false);
    }
  };

  const updateForm = (patch) => {
    setForm((prev) => {
      const next = { ...prev, ...patch };
      // Fire-and-forget preview refresh with the *next* values.
      fetchPreview(next);
      return next;
    });
  };

  const approveAllPending = async () => {
    setApproving(true);
    try {
      const { data } = await api.post("/timesheets/approve-range", form);
      toast.success(`Approved ${data.approved} timesheet${data.approved === 1 ? "" : "s"}`);
      await fetchPreview();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setApproving(false);
    }
  };

  const create = async () => {
    try {
      await api.post("/invoices/generate", form);
      toast.success("Invoice generated");
      setOpen(false); setPreview(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadPdf = async (inv) => {
    try {
      const res = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
      await savePdf(res.data, inv.pdf_filename);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onDelete = async (id) => {
    if (!confirm("Delete invoice? Linked timesheets will be released.")) return;
    await api.delete(`/invoices/${id}`); toast.success("Deleted"); load();
  };

  const togglePaid = async (inv) => {
    await api.post(`/invoices/${inv.id}/mark-paid`, { paid: !inv.paid });
    load();
  };

  return (
    <div className="space-y-5 max-w-6xl" data-testid="invoices-page">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-4xl font-display font-black tracking-tight">Invoices</h1>
          <p className="text-muted-foreground">Generate weekly PDFs combining the cover letter and invoice.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="new-invoice-btn"
          className="rounded-full min-h-[48px] font-bold gap-2" style={{ background: "hsl(var(--client))" }}>
          <Plus className="w-4 h-4" /> Generate weekly invoice
        </Button>
      </header>

      <Card className="rounded-3xl border-2 overflow-hidden" data-testid="invoices-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wider font-bold">
              <tr><th className="text-left p-3">Number</th><th className="text-left p-3">Worker</th><th className="text-left p-3">Week</th><th className="text-left p-3">Total</th><th className="text-left p-3">Status</th><th className="text-right p-3">Actions</th></tr>
            </thead>
            <tbody>
              {invs.map((i) => (
                <tr key={i.id} className="border-t" data-testid={`inv-row-${i.id}`}>
                  <td className="p-3 font-mono font-bold">{i.invoice_number}</td>
                  <td className="p-3">{i.worker_name}</td>
                  <td className="p-3 text-xs">{i.week_start}<br/>→ {i.week_end}</td>
                  <td className="p-3 font-mono font-bold">${i.total.toFixed(2)}</td>
                  <td className="p-3">
                    <Badge variant={i.paid ? "default" : "secondary"} className="rounded-full font-bold uppercase tracking-wider text-[10px]">{i.paid ? "Paid" : i.status}</Badge>
                  </td>
                  <td className="p-3 flex gap-1 justify-end">
                    <Button size="sm" variant="ghost" className="rounded-full" onClick={() => downloadPdf(i)} data-testid={`download-${i.id}`}>
                      <Download className="w-4 h-4" />
                    </Button>
                    {/* Email is allowed for BOTH client and worker — but only once the invoice is approved. */}
                    {i.status === "approved" && (
                      <Button size="sm" variant="ghost" className="rounded-full" onClick={() => setEmailOpen(i)}
                              data-testid={`email-${i.id}`} title="Email invoice">
                        <Mail className="w-4 h-4" />
                      </Button>
                    )}
                    {isClient && (
                      <>
                        <Button size="sm" variant="ghost" className={`rounded-full ${i.paid ? "text-emerald-700" : ""}`} onClick={() => togglePaid(i)} data-testid={`paid-${i.id}`}>
                          <CheckCircle2 className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="rounded-full text-destructive" onClick={() => onDelete(i.id)} data-testid={`delete-inv-${i.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {invs.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-muted-foreground">No invoices yet. Generate one above.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setPreview(null); else fetchPreview(); }}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="generate-invoice-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Generate invoice</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase font-bold tracking-wider">Worker</Label>
              <Select value={form.worker_id} onValueChange={(v) => updateForm({ worker_id: v })}>
                <SelectTrigger className="rounded-xl min-h-[48px]" data-testid="inv-worker-select"><SelectValue placeholder="Choose worker" /></SelectTrigger>
                <SelectContent>{workers.map((w) => <SelectItem key={w.id} value={w.id}>{w.full_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs uppercase font-bold tracking-wider">Week start</Label><Input type="date" value={form.week_start} onChange={(e) => updateForm({ week_start: e.target.value })} className="rounded-xl min-h-[48px]" data-testid="week-start" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase font-bold tracking-wider">Week end</Label><Input type="date" value={form.week_end} onChange={(e) => updateForm({ week_end: e.target.value })} className="rounded-xl min-h-[48px]" data-testid="week-end" /></div>
            </div>

            {/* Live preview — surfaces silently-excluded timesheets so the user never has to wonder why their invoice is short */}
            {previewing && (
              <div className="text-xs text-muted-foreground italic" data-testid="preview-loading">Checking timesheets…</div>
            )}
            {preview && !previewing && (
              <div className="rounded-2xl border-2 p-3 space-y-2 text-sm" data-testid="preview-panel">
                {/* Eligible bucket */}
                <div className="flex items-center justify-between" data-testid="preview-eligible">
                  <span className="font-bold">{preview.eligible.length} will be invoiced</span>
                  <span className="font-mono">${preview.eligible_total.toFixed(2)}</span>
                </div>

                {/* Pending — the actionable warning */}
                {preview.pending.length > 0 && (
                  <div className="rounded-xl bg-coral/10 border-2 border-coral/30 p-2.5 space-y-1.5" data-testid="preview-pending-warning">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-bold text-coral">
                        ⚠️ {preview.pending.length} pending approval · ${preview.pending_total.toFixed(2)}
                      </span>
                      {isClient && (
                        <Button
                          size="sm" type="button"
                          onClick={approveAllPending} disabled={approving}
                          className="rounded-full font-bold h-7 text-xs px-3"
                          style={{ background: "hsl(var(--coral))", color: "white" }}
                          data-testid="approve-all-pending-btn"
                        >
                          {approving ? "Approving…" : "Approve all & include"}
                        </Button>
                      )}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      These shifts won&apos;t be invoiced until you approve them.
                      {!isClient && " Ask your client to approve them in Notifications."}
                    </div>
                  </div>
                )}

                {/* Already-invoiced — informational, no action */}
                {preview.already_invoiced.length > 0 && (
                  <div className="text-xs text-muted-foreground" data-testid="preview-already-invoiced">
                    🚫 {preview.already_invoiced.length} already belong to another invoice
                  </div>
                )}

                {/* Rejected — informational */}
                {preview.rejected.length > 0 && (
                  <div className="text-xs text-muted-foreground" data-testid="preview-rejected">
                    🚫 {preview.rejected.length} rejected — won&apos;t be invoiced
                  </div>
                )}

                {/* Empty state hint */}
                {preview.eligible.length === 0 && preview.pending.length === 0 &&
                  preview.already_invoiced.length === 0 && preview.rejected.length === 0 && (
                  <div className="text-xs text-muted-foreground italic" data-testid="preview-empty">
                    No timesheets found in this range.
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              onClick={create}
              disabled={!preview || preview.eligible.length === 0}
              className="rounded-full font-bold"
              style={{ background: "hsl(var(--client))" }}
              data-testid="generate-invoice-submit"
            >
              {preview && preview.eligible.length > 0
                ? `Generate (${preview.eligible.length} shifts · $${preview.eligible_total.toFixed(2)})`
                : "Generate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {emailOpen && (
        <EmailRecipientDialog
          open={!!emailOpen}
          onOpenChange={(o) => !o && setEmailOpen(null)}
          title={`Email invoice ${emailOpen.invoice_number}`}
          description="Pick recipients from your address book or add new ones. The invoice PDF will be attached."
          onSend={async (recipients) => {
            const res = await api.get(`/invoices/${emailOpen.id}/pdf`, { responseType: "blob" });
            const filename = emailOpen.pdf_filename || `invoice_${emailOpen.invoice_number}.pdf`;
            const result = await shareOrMailtoPdf({
              blob: res.data,
              filename,
              recipients,
              subject: `Invoice ${emailOpen.invoice_number}`,
              body: `Hi,\n\nPlease find invoice ${emailOpen.invoice_number} attached.\n\nKind regards.`,
            });
            if (result === "shared") toast.success("Opened in your email app");
            else if (result === "mailto") toast.success(`PDF saved as “${filename}” in Downloads. Attach it in the email composer that just opened.`, { duration: 12000 });
          }}
        />
      )}
    </div>
  );
}
